import { request } from './client'
/* The SAME line M-21 draws. A second shape for "what linen did this room take"
   would disagree with the handset the first time a standard moved. */
import type { TaskLinenLine } from './checklist'

/* The assignment board read model, as the server sends it. */

export type TaskState =
  | 'CREATED' | 'ASSIGNED' | 'STARTED' | 'SUBMITTED' | 'REWORK' | 'COMPLETED' | 'CANCELLED'

export type RosterState = 'ON_SHIFT' | 'REST_DAY' | 'LEAVE' | 'ABSENT'

export interface BoardTask {
  taskId: string
  roomId: string | null
  roomNumber: string
  /** **WHICH BUILDING.** Faiz e Husaini's four Najaf buildings each number from 101, so
   *  a card that says only "101" names four different doors. Both names travel with it
   *  because the card is drawn in whichever language the reader chose. */
  buildingCode: string
  buildingName: string
  buildingNameAr: string | null
  floorCode: string
  roomType: string
  roomTypeAr: string | null
  taskTypeCode: string
  taskTypeName: string
  taskTypeNameAr: string | null
  /** The credit value the task was RAISED at, not the type's current value. */
  credits: number
  state: TaskState
  priority: number
  firstStartedAtUtc: string | null
  submittedAtUtc: string | null
  /** **WHY THIS CARD CANNOT BE HANDED TO ANYBODY.** Empty for almost every card.
   *
   *  DEFECT #116. Sprint 33's sweep raises a card for a room nobody has configured, on
   *  purpose — *the card is the discovery* — and the card looked like any other: "110 ·
   *  Departure clean · Suite · 0.00 cr". Zero credits reads as a light room, not an
   *  unusable one, and a supervisor drags it into a refusal the card gave them no way to
   *  see coming.
   *
   *  FROM THE SERVER, from the same `CleaningRules.Missing` the assignment gate uses —
   *  not derived here from `credits === 0`, which would be a second opinion about the
   *  rule and is anyway the symptom rather than the definition. */
  missingCleaningRules: string[]
}

/**
 * NOTE WHAT IS ABSENT: no `workloadCredits`.
 *
 * The lane shows "6.5 / 8.0 cr" and that figure is the sum of the cards drawn in it.
 * A server-computed total sent beside the cards would be a second number for one fact,
 * and the first time the two differ by one card the supervisor stops believing either.
 * `creditBudget` is server state — it comes from the shift — and the client cannot
 * derive it, which is why that one IS sent.
 */
export interface BoardLane {
  userId: string
  /** Set when this lane is a PAIR rather than a man. `displayName` is both their names.
   *  A lane is one or the other, never both — see `BoardLane` on the server. */
  teamId: string | null
  displayName: string
  displayNameAr: string | null
  rosterState: RosterState
  shiftCode: string | null
  shiftName: string | null
  shiftNameAr: string | null
  creditBudget: number | null
  coverage: string | null
  tasks: BoardTask[]
}

export interface AssignmentBoardView {
  propertyId: string
  /** Today AT THE HOTEL. For three hours of every night this is not the server's date. */
  localDate: string
  generatedAtUtc: string
  lanes: BoardLane[]
  unassigned: BoardTask[]
  /** **WHEN THE MORNING SWEEP RUNS BY ITSELF**, as minutes past LOCAL midnight — null when
   *  the hotel presses the button. Sprint 34, CQ-135. Sent because the board both shows it
   *  and sets it: whether the board fills itself is not something a supervisor should have
   *  to remember. */
  generateWorkAtLocalMinute: number | null
  /** **TODAY'S SCHEDULED RUN SHOULD HAVE HAPPENED AND HAS NOT.**
   *
   *  Sprint 33 argued against having a scheduler at all, on the grounds that a hotel whose
   *  board is empty at 06:30 needs to know whether a job failed or whether there is nothing
   *  to do. This is that objection answered: from the same `WorkSchedule.IsOverdue` the
   *  scheduler's own loop reads, so the screen and the job cannot disagree about whether
   *  the hotel is covered. Always false when no time is set. */
  scheduledRunIsOverdue: boolean
}

export function fetchAssignmentBoard(
  propertyId: string,
  options: { signal?: AbortSignal } = {},
): Promise<AssignmentBoardView> {
  return request<AssignmentBoardView>(
    `/properties/${propertyId}/tasks/assignment-board`,
    options.signal ? { signal: options.signal } : {})
}

export function assignTask(
  taskId: string,
  body: { assignToUserId: string; reason?: string },
): Promise<void> {
  return request<void>(`/tasks/${taskId}/assign`, { method: 'POST', body })
}

export function inspectTask(
  taskId: string,
  body: {
    passed: boolean
    reason?: string
    /** Which lines the inspector marked. Empty on a pass. */
    failures?: { checklistItemId: string; note?: string }[]
  },
): Promise<void> {
  return request<void>(`/tasks/${taskId}/inspect`, { method: 'POST', body })
}

/** What the handset opens on: one attendant's own rooms for today. */
export interface MyWorkView {
  userId: string
  localDate: string
  generatedAtUtc: string
  rosterState: RosterState
  tasks: BoardTask[]
}

export function fetchMyWork(
  userId: string,
  options: { signal?: AbortSignal } = {},
): Promise<MyWorkView> {
  return request<MyWorkView>(
    `/users/${userId}/work`,
    options.signal ? { signal: options.signal } : {})
}

/* ── WHERE THE DAY'S WORK COMES FROM (Sprint 33) ──────────────────────────────
   Until this sprint the product could assign, start, submit and inspect a task and had
   no way to create one — defect #108. Two paths, and they are genuinely different: the
   morning sweep raises the hotel's whole day, and a supervisor raises ones and twos. */

/** What the sweep did, in the terms a supervisor asks the question in. */
export interface DayGenerated {
  /** The hotel's own working day — not the browser's. */
  localDate: string
  /** Created by THIS run. Zero on a second press, which is correct. */
  raised: number
  departures: number
  stayovers: number
  /** Rooms that needed work and already had some open. Explains a `raised` of zero. */
  alreadyInHand: number
  notInService: number
  nothingToClean: number
  /** Every room swept. `raised + alreadyInHand + notInService + nothingToClean` equals
   *  this — sent so the claim is checkable rather than asserted. */
  roomsSwept: number
  /** **THE NUMBER THAT MATTERS MOST.** Room NUMBERS, not a count: rooms whose work was
   *  raised and which nobody can be given, because nobody has said how they are cleaned.
   *  The card is created anyway — a room silently left out of the day is a room nobody
   *  discovers until a guest opens the door — and this is so the supervisor learns it at
   *  six in the morning rather than at eleven from a refusal. */
  unassignableRooms: string[]
  /** CQ-26's third trigger — deep cleans raised because a room's own frequency came
   *  round. Sprint 83. */
  deepCleans: number
  /** CQ-26's fourth — touch-ups for a made-up room that had stood too long. */
  refreshes: number
  /** **WHAT THIS HOTEL HAS SWITCHED OFF, AND HOW MANY ROOMS WANTED IT.** A sweep that
   *  simply raised less work would leave a supervisor reading a thin board with nothing to
   *  explain it. */
  switchedOff: { code: string; rooms: number }[]
}

/**
 * **SWEEP THE HOTEL FOR TODAY'S WORK.**
 *
 * SAFE TO CALL TWICE. A room with open work of the kind it needs is left alone, and the
 * database refuses a second generated task for one room, kind and day even under a race —
 * so a slow page and an impatient second press changes nothing.
 *
 * `localDate` is only for re-running a day after the fact; omitting it means today, in the
 * property's own time zone, which is what pressing the button means.
 */
export const generateDayWork = (propertyId: string, localDate?: string) =>
  request<DayGenerated>(`/properties/${propertyId}/work/generate`, {
    method: 'POST', body: localDate ? { localDate } : {},
  })

/** What raising one task cost — returned rather than refetched. */
export interface TaskRaised {
  taskId: string
  estimatedMinutes: number
  credits: number
}

/**
 * **RAISE ONE PIECE OF WORK BY HAND.** A touch-up because a guest complained, a deep clean
 * because a room is free this afternoon, a second departure clean because somebody checked
 * out at four.
 *
 * A ROOM XOR AN AREA. The reason is not optional and is not for the audit: the person who
 * has to act on it — an attendant looking at a card that was not there an hour ago — is the
 * one who needs to know why.
 */
export const raiseTask = (
  propertyId: string,
  body: {
    roomId?: string
    areaId?: string
    taskTypeCode: string
    reason?: string
    priority?: number
  },
) => request<TaskRaised>(`/properties/${propertyId}/tasks`, { method: 'POST', body })

/**
 * **T7 — CANCEL WORK.** The way out, built in the same sprint as the way in.
 *
 * A reason is required and the act lands on W-124, because cancelling takes work off
 * somebody else's morning, possibly mid-round.
 */
export const cancelTask = (taskId: string, reason: string) =>
  request<void>(`/tasks/${taskId}/cancel`, { method: 'POST', body: { reason } })

/**
 * **SET OR CLEAR THE TIME THE MORNING SWEEP RUNS BY ITSELF.** Minutes past local midnight.
 *
 * `null` turns it off and gives the hotel the button back — a real intention rather than an
 * omission, which is why this endpoint has one field and setting it is the whole act.
 */
export const setWorkSchedule = (propertyId: string, localMinute: number | null) =>
  request<void>(`/properties/${propertyId}/work/schedule`, {
    method: 'POST', body: { localMinute },
  })

/* ── W-31 · ONE PIECE OF WORK, AND EVERYTHING THAT HAPPENED TO IT (Sprint 80) ──

   Every other screen shows a task as a CARD — a room number, a state, a credit figure.
   That is right for a board and useless for the one moment somebody is asking about a
   particular task: "this room was rejected twice — what actually happened to it?" */

export interface TaskHolder {
  assignmentId: string
  /** One name, or two joined for a pair. **A pair is not a thing with a name.** */
  who: string
  whoAr: string | null
  givenBy: string | null
  assignedAtUtc: string
  unassignedAtUtc: string | null
  /** Why it moved. **Never translated** — somebody wrote it about somebody. */
  reason: string | null
}

export interface TaskAnswerLine {
  checklistItemId: string
  text: string
  textAr: string | null
  /** `YES`, `NO`, or null where it was never answered. */
  answer: string | null
  note: string | null
  photographs: number
}

export interface TaskDetailView {
  taskId: string
  propertyId: string
  taskTypeCode: string
  state: string
  roomId: string | null
  roomNumber: string | null
  areaName: string | null
  areaNameAr: string | null
  estimatedMinutes: number
  /** **Snapshotted when the task was raised**, not looked up. */
  credits: number
  forLocalDate: string
  /** **The FIRST start, and it survives a rejection.** A room cleaned badly, rejected and
   *  cleaned again took as long as it took. */
  firstStartedAtUtc: string | null
  submittedAtUtc: string | null
  completedAtUtc: string | null
  /** What raised it — the morning sweep, or null where a person did. */
  generatedByTrigger: string | null
  held: TaskHolder[]
  checklist: TaskAnswerLine[]
  /** The same lines M-21 draws, from the same service. */
  linen: TaskLinenLine[]
}

export const fetchTaskDetail = (
  taskId: string, options: { signal?: AbortSignal } = {},
) => request<TaskDetailView>(
  `/tasks/${taskId}`,
  options.signal ? { signal: options.signal } : {})
